// The organizations the user belongs to, the active one marked; picking
// another makes it active

import { useEffect, useRef } from 'react';
import { toast } from 'react-toastify';
import { useFirebase } from './FirebaseContextProvider';
import { useModal } from './ModalProvider';
import Fieldset from './Fieldset';
import RadioButton from './RadioButton';
import Spinner from './Spinner';
import './OrganizationSwitchModalBody.scss';

export default function OrganizationSwitchModalBody() {
  const { activeOrg, myOrgs, orgsById, setOrganization, isOrgSwitching } =
    useFirebase();
  const { closeModal, isModalOpen } = useModal();
  // Whether the window still shows this body when a switch answers. The body
  // outlives its closed modal, and closeModal shuts whichever one is open now.
  const shown = useRef(false);

  useEffect(() => {
    shown.current = isModalOpen;
    return () => {
      shown.current = false;
    };
  }, [isModalOpen]);

  const handleChange = async (organization) => {
    // Not local state: a window reopened mid-switch is a fresh body. A second
    // write behind the first lands the listeners on whichever answers last.
    if (isOrgSwitching) return;

    if (organization === activeOrg) return closeModal();

    try {
      // Nothing is stored locally — the switch is announced to the provider,
      // which drops its organization-scoped listeners, writes, rebuilds them
      await setOrganization(organization);
      if (shown.current) closeModal();
    } catch (error) {
      toast.error(error?.message ?? 'Failed to switch organization');
    }
  };

  return (
    <div className='organization-switch-modal'>
      <Fieldset label='Organizations'>
        {myOrgs.map((id) => (
          <RadioButton
            key={id}
            label={orgsById.get(id)?.name ?? id}
            name='organization'
            value={id}
            selectedValue={activeOrg}
            onChange={handleChange}
          />
        ))}
      </Fieldset>
      <p className='hint font-14'>
        You only see teams, tournaments and users of the selected organization.
      </p>
      <Spinner show={isOrgSwitching} />
    </div>
  );
}
